import React, { FC, memo } from 'react';
import classNames from 'classnames';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { FieldArray, useFormikContext } from 'formik';
// Import Types
import type { JSONFormBuilderFormikContextType } from '../../types';
// Import Constants
import l from 'constants/localizationLabels';
// Import Components
import { Button, ExtendedInput, ModuleTitle } from '@geneui/components';
import KeyValueFormFields from '../KeyValueFormFields';
// Import Styles
import styles from './dynamicNestedFormBuilder.module.scss';

interface Props {
    isNested?: boolean;
    title?: string;
    formKey: string;
    fieldValueType?: 'text' | 'number';
    parentFormKey?: string;
    disabled?: boolean;
    shouldNotBeNested?: boolean;
    handleRemoveParent?: () => void;
    handleCloneParent?: () => void;
}

const DynamicNestedFormBuilder: FC<Props> = (props) => {
    const {
        isNested,
        title,
        formKey,
        fieldValueType = 'text',
        parentFormKey,
        disabled,
        shouldNotBeNested,
        handleRemoveParent,
        handleCloneParent,
    } = props;
    const { t } = useTranslation();
    const { values, errors, touched, handleChange, handleBlur } = useFormikContext<JSONFormBuilderFormikContextType>();
    const fieldValues = get(values, formKey);

    return (
        <FieldArray
            key={formKey}
            name={formKey}
            render={({ push, insert, handleRemove }) => {
                const initialValue = fieldValueType === 'number' ? null : '';

                const handleClone = (index: number) => () => {
                    const clonedField = fieldValues[index];
                    insert(index + 1, clonedField);
                };

                const handleAddField = () => {
                    push(['', initialValue]);
                };

                const handleAddNestedFields = () => {
                    push(['', [['', initialValue]]]);
                };

                return (
                    <div className={classNames(styles.container, { [styles.withTitle]: title })}>
                        {title && <ModuleTitle title={t(title)} />}

                        <div
                            className={classNames(styles.wrapper, {
                                [styles.nested]: isNested,
                                [styles.withTitle]: title,
                            })}
                        >
                            {isNested && (
                                <ModuleTitle title={t(l.NestedFields)}>
                                    {!disabled && (
                                        <>
                                            <Button
                                                flexibility="default"
                                                appearance="minimal"
                                                icon="bc-icon-clone"
                                                onClick={handleCloneParent}
                                                children={undefined}
                                            />
                                            <Button
                                                flexibility="default"
                                                appearance="minimal"
                                                icon="bc-icon-trash"
                                                onClick={handleRemoveParent}
                                                children={undefined}
                                            />
                                        </>
                                    )}
                                </ModuleTitle>
                            )}

                            <div className={classNames({ [styles.nestedFieldsWrapper]: isNested })}>
                                {!disabled && (
                                    <div className={styles.actionButtons}>
                                        <Button
                                            flexibility="default"
                                            appearance="outline"
                                            size="medium"
                                            icon="bc-icon-plus"
                                            onClick={handleAddField}
                                        >
                                            {t(l.AddField)}
                                        </Button>
                                        {!shouldNotBeNested && (
                                            <Button
                                                flexibility="default"
                                                appearance="outline"
                                                size="medium"
                                                icon="bc-icon-plus"
                                                onClick={handleAddNestedFields}
                                            >
                                                {t(l.AddNestedFields)}
                                            </Button>
                                        )}
                                    </div>
                                )}

                                {isNested && parentFormKey && (
                                    <ExtendedInput
                                        type="text"
                                        name={parentFormKey}
                                        label="Group Key"
                                        title="Group Key"
                                        placeholder="Group Key"
                                        labelAppearance="swap"
                                        value={get(values, parentFormKey)}
                                        className={classNames(styles.nestedFieldsParentKeyInput, {
                                            [styles.disabled]: disabled,
                                        })}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        errorText={get(errors, parentFormKey)}
                                        isValid={get(touched, parentFormKey) ? !get(errors, parentFormKey) : undefined}
                                        disabled={disabled}
                                    />
                                )}

                                {!!fieldValues?.length && (
                                    <div
                                        className={classNames(styles.fieldsContainer, {
                                            [styles.nested]: isNested,
                                            [styles.disabled]: disabled,
                                        })}
                                    >
                                        {fieldValues.map((fieldValue, index) => (
                                            <KeyValueFormFields
                                                key={index}
                                                index={index}
                                                fieldValue={fieldValue}
                                                formFieldKey={formKey}
                                                fieldValueType={fieldValueType}
                                                isNested={isNested}
                                                disabled={disabled}
                                                shouldNotBeNested={shouldNotBeNested}
                                                handleRemove={handleRemove(index)}
                                                handleClone={handleClone(index)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            }}
        />
    );
};

export default memo(DynamicNestedFormBuilder);
